/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * rubricaContatti.ts
 * Rubrica destinatari per invio scheda COAU via email.
 * Precaricata con Protezione Civile provinciale italiana.
 */

export interface ContattoRubrica {
  id: string;
  nome: string;           // es. "Protezione Civile Catania"
  email: string;
  regione: string;
  provincia: string;
  sigla: string;          // sigla provincia es. "CT"
  custom?: boolean;       // true = aggiunto manualmente dall'utente
  note?: string;
}

// Province italiane con sigla e capoluogo
const PROVINCE_ITALIA: { provincia: string; sigla: string; regione: string }[] = [
  // SICILIA
  { provincia: 'agrigento',     sigla: 'AG', regione: 'Sicilia' },
  { provincia: 'caltanissetta', sigla: 'CL', regione: 'Sicilia' },
  { provincia: 'catania',       sigla: 'CT', regione: 'Sicilia' },
  { provincia: 'enna',          sigla: 'EN', regione: 'Sicilia' },
  { provincia: 'messina',       sigla: 'ME', regione: 'Sicilia' },
  { provincia: 'palermo',       sigla: 'PA', regione: 'Sicilia' },
  { provincia: 'ragusa',        sigla: 'RG', regione: 'Sicilia' },
  { provincia: 'siracusa',      sigla: 'SR', regione: 'Sicilia' },
  { provincia: 'trapani',       sigla: 'TP', regione: 'Sicilia' },
  // CALABRIA
  { provincia: 'catanzaro',     sigla: 'CZ', regione: 'Calabria' },
  { provincia: 'cosenza',       sigla: 'CS', regione: 'Calabria' },
  { provincia: 'crotone',       sigla: 'KR', regione: 'Calabria' },
  { provincia: 'reggio-calabria', sigla: 'RC', regione: 'Calabria' },
  { provincia: 'vibo-valentia', sigla: 'VV', regione: 'Calabria' },
  // CAMPANIA
  { provincia: 'avellino',      sigla: 'AV', regione: 'Campania' },
  { provincia: 'benevento',     sigla: 'BN', regione: 'Campania' },
  { provincia: 'caserta',       sigla: 'CE', regione: 'Campania' },
  { provincia: 'napoli',        sigla: 'NA', regione: 'Campania' },
  { provincia: 'salerno',       sigla: 'SA', regione: 'Campania' },
  // PUGLIA
  { provincia: 'bari',          sigla: 'BA', regione: 'Puglia' },
  { provincia: 'barletta-andria-trani', sigla: 'BT', regione: 'Puglia' },
  { provincia: 'brindisi',      sigla: 'BR', regione: 'Puglia' },
  { provincia: 'foggia',        sigla: 'FG', regione: 'Puglia' },
  { provincia: 'lecce',         sigla: 'LE', regione: 'Puglia' },
  { provincia: 'taranto',       sigla: 'TA', regione: 'Puglia' },
  // BASILICATA
  { provincia: 'matera',        sigla: 'MT', regione: 'Basilicata' },
  { provincia: 'potenza',       sigla: 'PZ', regione: 'Basilicata' },
  // SARDEGNA
  { provincia: 'cagliari',      sigla: 'CA', regione: 'Sardegna' },
  { provincia: 'nuoro',         sigla: 'NU', regione: 'Sardegna' },
  { provincia: 'oristano',      sigla: 'OR', regione: 'Sardegna' },
  { provincia: 'sassari',       sigla: 'SS', regione: 'Sardegna' },
  { provincia: 'sud-sardegna',  sigla: 'SU', regione: 'Sardegna' },
  // LAZIO
  { provincia: 'frosinone',     sigla: 'FR', regione: 'Lazio' },
  { provincia: 'latina',        sigla: 'LT', regione: 'Lazio' },
  { provincia: 'rieti',         sigla: 'RI', regione: 'Lazio' },
  { provincia: 'roma',          sigla: 'RM', regione: 'Lazio' },
  { provincia: 'viterbo',       sigla: 'VT', regione: 'Lazio' },
  // TOSCANA
  { provincia: 'arezzo',        sigla: 'AR', regione: 'Toscana' },
  { provincia: 'firenze',       sigla: 'FI', regione: 'Toscana' },
  { provincia: 'grosseto',      sigla: 'GR', regione: 'Toscana' },
  { provincia: 'livorno',       sigla: 'LI', regione: 'Toscana' },
  { provincia: 'lucca',         sigla: 'LU', regione: 'Toscana' },
  { provincia: 'massa-carrara', sigla: 'MS', regione: 'Toscana' },
  { provincia: 'pisa',          sigla: 'PI', regione: 'Toscana' },
  { provincia: 'pistoia',       sigla: 'PT', regione: 'Toscana' },
  { provincia: 'prato',         sigla: 'PO', regione: 'Toscana' },
  { provincia: 'siena',         sigla: 'SI', regione: 'Toscana' },
  // EMILIA-ROMAGNA
  { provincia: 'bologna',       sigla: 'BO', regione: 'Emilia-Romagna' },
  { provincia: 'ferrara',       sigla: 'FE', regione: 'Emilia-Romagna' },
  { provincia: 'forli-cesena',  sigla: 'FC', regione: 'Emilia-Romagna' },
  { provincia: 'modena',        sigla: 'MO', regione: 'Emilia-Romagna' },
  { provincia: 'parma',         sigla: 'PR', regione: 'Emilia-Romagna' },
  { provincia: 'piacenza',      sigla: 'PC', regione: 'Emilia-Romagna' },
  { provincia: 'ravenna',       sigla: 'RA', regione: 'Emilia-Romagna' },
  { provincia: 'reggio-emilia', sigla: 'RE', regione: 'Emilia-Romagna' },
  { provincia: 'rimini',        sigla: 'RN', regione: 'Emilia-Romagna' },
  // LOMBARDIA
  { provincia: 'bergamo',       sigla: 'BG', regione: 'Lombardia' },
  { provincia: 'brescia',       sigla: 'BS', regione: 'Lombardia' },
  { provincia: 'como',          sigla: 'CO', regione: 'Lombardia' },
  { provincia: 'cremona',       sigla: 'CR', regione: 'Lombardia' },
  { provincia: 'lecco',         sigla: 'LC', regione: 'Lombardia' },
  { provincia: 'lodi',          sigla: 'LO', regione: 'Lombardia' },
  { provincia: 'mantova',       sigla: 'MN', regione: 'Lombardia' },
  { provincia: 'milano',        sigla: 'MI', regione: 'Lombardia' },
  { provincia: 'monza-brianza', sigla: 'MB', regione: 'Lombardia' },
  { provincia: 'pavia',         sigla: 'PV', regione: 'Lombardia' },
  { provincia: 'sondrio',       sigla: 'SO', regione: 'Lombardia' },
  { provincia: 'varese',        sigla: 'VA', regione: 'Lombardia' },
  // PIEMONTE
  { provincia: 'alessandria',   sigla: 'AL', regione: 'Piemonte' },
  { provincia: 'asti',          sigla: 'AT', regione: 'Piemonte' },
  { provincia: 'biella',        sigla: 'BI', regione: 'Piemonte' },
  { provincia: 'cuneo',         sigla: 'CN', regione: 'Piemonte' },
  { provincia: 'novara',        sigla: 'NO', regione: 'Piemonte' },
  { provincia: 'torino',        sigla: 'TO', regione: 'Piemonte' },
  { provincia: 'verbano-cusio-ossola', sigla: 'VB', regione: 'Piemonte' },
  { provincia: 'vercelli',      sigla: 'VC', regione: 'Piemonte' },
  // VENETO
  { provincia: 'belluno',       sigla: 'BL', regione: 'Veneto' },
  { provincia: 'padova',        sigla: 'PD', regione: 'Veneto' },
  { provincia: 'rovigo',        sigla: 'RO', regione: 'Veneto' },
  { provincia: 'treviso',       sigla: 'TV', regione: 'Veneto' },
  { provincia: 'venezia',       sigla: 'VE', regione: 'Veneto' },
  { provincia: 'verona',        sigla: 'VR', regione: 'Veneto' },
  { provincia: 'vicenza',       sigla: 'VI', regione: 'Veneto' },
  // ALTRE REGIONI
  { provincia: 'ancona',        sigla: 'AN', regione: 'Marche' },
  { provincia: 'ascoli-piceno', sigla: 'AP', regione: 'Marche' },
  { provincia: 'fermo',         sigla: 'FM', regione: 'Marche' },
  { provincia: 'macerata',      sigla: 'MC', regione: 'Marche' },
  { provincia: 'pesaro-urbino', sigla: 'PU', regione: 'Marche' },
  { provincia: "l-aquila",      sigla: 'AQ', regione: 'Abruzzo' },
  { provincia: 'chieti',        sigla: 'CH', regione: 'Abruzzo' },
  { provincia: 'pescara',       sigla: 'PE', regione: 'Abruzzo' },
  { provincia: 'teramo',        sigla: 'TE', regione: 'Abruzzo' },
  { provincia: 'campobasso',    sigla: 'CB', regione: 'Molise' },
  { provincia: 'isernia',       sigla: 'IS', regione: 'Molise' },
  { provincia: 'perugia',       sigla: 'PG', regione: 'Umbria' },
  { provincia: 'terni',         sigla: 'TR', regione: 'Umbria' },
  { provincia: 'genova',        sigla: 'GE', regione: 'Liguria' },
  { provincia: 'imperia',       sigla: 'IM', regione: 'Liguria' },
  { provincia: 'la-spezia',     sigla: 'SP', regione: 'Liguria' },
  { provincia: 'savona',        sigla: 'SV', regione: 'Liguria' },
  { provincia: 'trento',        sigla: 'TN', regione: 'Trentino-Alto Adige' },
  { provincia: 'bolzano',       sigla: 'BZ', regione: 'Trentino-Alto Adige' },
  { provincia: 'gorizia',       sigla: 'GO', regione: 'Friuli-Venezia Giulia' },
  { provincia: 'pordenone',     sigla: 'PN', regione: 'Friuli-Venezia Giulia' },
  { provincia: 'trieste',       sigla: 'TS', regione: 'Friuli-Venezia Giulia' },
  { provincia: 'udine',         sigla: 'UD', regione: 'Friuli-Venezia Giulia' },
  { provincia: 'aosta',         sigla: 'AO', regione: "Valle d'Aosta" },
];

// Genera rubrica base da schema provinciale
export const RUBRICA_DEFAULT: ContattoRubrica[] = PROVINCE_ITALIA.map(p => ({
  id: `prov-${p.sigla.toLowerCase()}`,
  nome: `Protezione Civile ${p.provincia.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`,
  email: `protezione.civile@pec.provincia.${p.provincia}.it`,
  regione: p.regione,
  provincia: p.provincia.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  sigla: p.sigla,
}));

// Contatti speciali Corpo Forestale Regione Sicilia
export const CONTATTI_SPECIALI: ContattoRubrica[] = [
  {
    id: 'forestale-sicilia-bonsangue',
    nome: 'Dott. Bonsangue — Corpo Forestale Sicilia',
    email: 'sbonsangue.foreste@regione.sicilia.it',
    regione: 'Sicilia',
    provincia: 'Palermo',
    sigla: 'PA',
    note: 'Corpo Forestale Regione Sicilia',
  },
  {
    id: 'forestale-sicilia-cordaro',
    nome: 'CC Cordaro — Corpo Forestale Sicilia',
    email: 'acordaro.foreste@regione.sicilia.it',
    regione: 'Sicilia',
    provincia: 'Palermo',
    sigla: 'PA',
    note: 'Corpo Forestale Regione Sicilia',
  },
];

// Chiave localStorage per contatti custom
const STORAGE_KEY = 'dos518_rubrica_custom';

export function loadContattiCustom(): ContattoRubrica[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveContattiCustom(contatti: ContattoRubrica[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(contatti));
}

export function getRubricaCompleta(): ContattoRubrica[] {
  return [
    ...CONTATTI_SPECIALI,
    ...loadContattiCustom(),
    ...RUBRICA_DEFAULT,
  ];
}
